import { Component, ElementRef, HostListener, NgZone, ViewChild } from '@angular/core';

type Bubble = {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  color: string;
};

@Component({
  selector: 'app-bubbles-bg',
  templateUrl: './bubbles-bg.component.html',
  styleUrls: ['./bubbles-bg.component.scss']
})

export class BubblesBgComponent {
  @ViewChild('bgCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private bubbles: Bubble[] = [];
  private rafId = 0;
  private width = 0;
  private height = 0;

  // ultra-light palette sampled from your background (lavender, blue/white, peach)
  // You can tweak alpha by changing the “0.55” below.
  private palette = [
    'rgba(232, 225, 255, 0.55)', // soft lavender
    'rgba(234, 243, 255, 0.55)', // airy blue
    'rgba(255, 239, 230, 0.55)', // soft peach
    'rgba(234, 253, 241, 0.55)', // minty white
  ];

  constructor(private zone: NgZone) { }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d', { alpha: true })!;
    this.ctx = ctx;

    this.resizeCanvas();
    this.seedBubbles();

    // smoother animation outside Angular
    this.zone.runOutsideAngular(() => {
      this.loop();
    });

    // pause when tab hidden to save CPU
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) cancelAnimationFrame(this.rafId);
      else this.loop();
    });
  }

  @HostListener('window:resize')
  onResize() {
    this.resizeCanvas();
    // keep bubbles inside bounds on resize
    this.bubbles.forEach(b => {
      b.x = Math.max(b.r, Math.min(this.width - b.r, b.x));
      b.y = Math.max(b.r, Math.min(this.height - b.r, b.y));
    });
  }

  private resizeCanvas() {
    const canvas = this.canvasRef.nativeElement;
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    this.width = canvas.clientWidth;
    this.height = canvas.clientHeight;
    canvas.width = Math.floor(this.width * dpr);
    canvas.height = Math.floor(this.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private seedBubbles() {
    const area = this.width * this.height;
    // scale count to the area (feels good across sizes)
    const count = Math.min(50, Math.max(18, Math.round(area / 30000)));

    this.bubbles = Array.from({ length: count }).map(() => this.makeBubble());
  }

  private makeBubble(spawnFromTop = false): Bubble {
    const maxR = Math.min(240, Math.max(80, Math.floor(Math.min(this.width, this.height) * 0.18)));
    const minR = Math.max(42, Math.floor(maxR * 0.35));
    const r = this.rand(minR, maxR);

    // faster but still smooth; slightly more downward than sideways
    const speed = this.rand(0.22, 0.85);
    const angle = this.rand(-Math.PI * 0.10, Math.PI * 0.10); // slight horizontal drif

    const yStart = spawnFromTop
      ? this.rand(-this.height * 0.25, -r)
      : this.rand(r, this.height - r);

    return {
      x: this.rand(r, this.width - r),
      y: yStart,
      r,
      vx: Math.sin(angle) * speed * 0.6, // small horizontal motion
      vy: Math.cos(angle) * speed + 0.15, // ensure downward bias
      color: this.palette[Math.floor(Math.random() * this.palette.length)],
    };
  }

  private loop = () => {
    this.rafId = requestAnimationFrame(this.loop);
    const ctx = this.ctx;

    // clear with slight fade for extra smoothness
    ctx.clearRect(0, 0, this.width, this.height);

    // soft additive blending for glow-y overlaps
    ctx.globalCompositeOperation = 'source-over';

    for (const b of this.bubbles) {
      // move
      b.x += b.vx;
      b.y += b.vy;

      // gentle wall bounce
      if (b.x - b.r <= 0 && b.vx < 0) b.vx *= -1;
      if (b.x + b.r >= this.width && b.vx > 0) b.vx *= -1;
      if (b.y - b.r <= 0 && b.vy < 0) b.vy *= -1;
      if (b.y + b.r >= this.height && b.vy > 0) b.vy *= -1;

      // very light “breathing”
      const t = performance.now() * 0.0003;
      const wobble = 1 + 0.02 * Math.sin(t + b.x * 0.01 + b.y * 0.01);
      const rr = b.r * wobble;

      // draw with a soft radial gradient + blur
      const g = ctx.createRadialGradient(b.x, b.y, rr * 0.15, b.x, b.y, rr);
      // center a bit brighter, very light edge
      g.addColorStop(0, b.color);
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');

      ctx.save();
      ctx.shadowBlur = Math.min(50, rr * 0.3);
      ctx.shadowColor = b.color;
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(b.x, b.y, rr, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // reset to default blend
    ctx.globalCompositeOperation = 'source-over';
  };

  private rand(min: number, max: number) {
    return Math.random() * (max - min) + min;
  }
}
